import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import camelCase from 'lodash-es/camelCase';
import { memo, MouseEventHandler } from 'react';

import getRatio from '../../lib/getRatio';
import normalizeCardTheme from '../../lib/normalizeCardTheme';

import CopyField from '../fields/CopyField';
import TextField from '../fields/TextField';
import ExternalLink from '../icons/ExternalLinkIcon';
import Image from '../partials/Image';
import CardDescriptionTypography from '../typography/CardDescription';
import CardTitle from '../typography/CardTitle';
import VerticalCard from './VerticalCard';

import styles from '../../styles/cards/promotion-vertical-card.module.css';

export interface PromotionVerticalCardProps extends Partial<Promotion> {
    ratio?: CardRatio;
    theme?: CardTheme | LegacyCardTheme | null;
    size?: CardSize;
    width?: number | null;
    imageLoading?: Loading;
    placeholder?: boolean;
    responsive?: boolean;
    withBorder?: boolean;
    className?: string | null;
    thumbnailClassName?: string | null;
    onClick?: MouseEventHandler | null;
}

const PromotionVerticalCard = ({
    id = null,
    handle = null,
    title = null,
    description = null,
    image = null,
    url = null,
    promoCode = null,
    theme: providedTheme = null,
    size = null,
    ratio = null,
    placeholder = false,
    responsive = false,
    imageLoading = undefined,
    withBorder = false,
    className = null,
    thumbnailClassName = null,
    onClick = null,
}: PromotionVerticalCardProps) => {
    const theme = normalizeCardTheme(providedTheme);
    const isBox = theme !== null && theme.indexOf('box') === 0;

    const defaultRatio = 16 / 9;
    const thumbnailRatio = getRatio(ratio, defaultRatio);

    const ContentComponent = promoCode !== null ? 'a' : 'div';

    const tracking = useTracking();
    const onCopyPromoCode = () => {
        tracking.trackPromotion({ id, handle, title }, 'copy_promo_code');
    };

    return (
        <VerticalCard
            href={url}
            external
            linkOnThumbnail={promoCode !== null}
            onClick={!placeholder ? onClick : null}
            thumbnail={
                <>
                    <Image
                        media={image}
                        className={styles.thumbnail}
                        pictureClassName={styles.picture}
                        size="medium"
                        loading={imageLoading}
                    />
                    <ExternalLink className={styles.icon} />
                </>
            }
            // height={isBox ? 'auto' : null}
            thumbnailRatio={thumbnailRatio}
            className={classNames([
                styles.container,
                {
                    [styles.withBorder]: withBorder && !isBox,
                    [styles.box]: isBox,
                },
                styles[camelCase(size)],
                styles[camelCase(ratio)],
                {
                    [styles.responsive]: responsive,
                },
                className,
            ])}
            thumbnailClassName={classNames([styles.thumbnailContainer, thumbnailClassName])}
            innerClassName={styles.inner}
            labelClassName={classNames([styles.label])}
        >
            <ContentComponent href={promoCode !== null ? url : null} className={styles.content}>
                <CardTitle className={styles.title} placeholder={placeholder} placeholderLines={3}>
                    {title}
                </CardTitle>
                {description !== null ? (
                    <CardDescriptionTypography
                        html={description}
                        className={styles.description}
                        placeholder={placeholder}
                        placeholderLines={3}
                    />
                ) : null}
            </ContentComponent>
            {promoCode !== null ? (
                <div className={styles.promoCode}>
                    <span className={styles.promoCodeLabel}>Code promo</span>
                    <CopyField
                        value={promoCode}
                        className={styles.promoCodeField}
                        onCopy={onCopyPromoCode}
                    >
                        <TextField
                            className={styles.promoCodeValue}
                            value={promoCode}
                            readOnly
                            selectOnFocus
                        />
                    </CopyField>
                </div>
            ) : null}
        </VerticalCard>
    );
};

export default memo(PromotionVerticalCard);
